import React, { useEffect, useMemo } from "react";
import {
  Background,
  BlockTitle,
  Page,
  Subpage,
  Images,
  Content,
  Title,
  Paragraph,
  InfoGrid,
  InfoItem,
  Note,
  SectionTitle,
  UnidadeBlock,
  SubLabel,
  Table,
  PlaceDate,
  SignatureRow,
} from "./style";
import Prefeitura from "../../assets/Images/prefeitura.jpg";
import Fiec from "../../assets/Images/fiec.png";
import PdfButtons from "../../components/PdfButtons";

const STORAGE_KEY = "declaracaoAcumuloPayload";

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();
}

function findUnidadeDetalhes(registro, unidades = []) {
  const unidadePorId = unidades.find((unidade) => String(unidade?.id) === String(registro?.id_unidade));

  if (unidadePorId) return unidadePorId;

  return unidades.find((unidade) => normalizeText(unidade?.nome) === normalizeText(registro?.unidade || registro?.nome_unidade));
}

function getUnidadeRegistro(registro, unidades = []) {
  const unidadeDetalhada = findUnidadeDetalhes(registro, unidades);

  return unidadeDetalhada?.nome || registro?.unidade || registro?.nome_unidade || "Sem unidade informada";
}

function getUnidadeDetalhes(registro, unidades = []) {
  const unidadeDetalhada = findUnidadeDetalhes(registro, unidades);
  const unidade = getUnidadeRegistro(registro, unidades);
  const cidade =
    registro?.cidade ||
    registro?.cidade_unidade ||
    (unidadeDetalhada?.cidade ? [unidadeDetalhada.cidade, unidadeDetalhada.estado].filter(Boolean).join("/") : "Indaiatuba/SP");

  return {
    unidade,
    nome: unidade,
    endereco: registro?.endereco || registro?.endereco_unidade || unidadeDetalhada?.endereco || "Não informado",
    cidade,
    cep: registro?.cep || registro?.cep_unidade || unidadeDetalhada?.cep || "Não informado",
    telefone: registro?.telefone || registro?.telefone_unidade || unidadeDetalhada?.telefone || "Não informado",
  };
}

function hasRelevantAulaData(aula) {
  return Boolean(
    aula?.id_unidade ||
    aula?.unidade ||
    aula?.nome_unidade ||
    aula?.disciplina ||
    aula?.turma ||
    aula?.dia_semana ||
    aula?.horario_inicio ||
    aula?.horario_fim ||
    aula?.carga ||
    aula?.numero_aulas
  );
}

function hasRelevantProjetoData(projeto) {
  return Boolean(
    projeto?.id_unidade ||
    projeto?.unidade ||
    projeto?.nome_unidade ||
    projeto?.projeto ||
    projeto?.dia_semana ||
    projeto?.horario_inicio ||
    projeto?.horario_fim ||
    projeto?.carga_horaria ||
    projeto?.carga
  );
}

function sanitizeCourseName(value) {
  return String(value || "")
    .replace(/\bCURSO\s+T[ÉE]CNICO\s+EM\b/gi, "")
    .replace(/\bCURSO\s+T[ÉE]CNICO\b/gi, "")
    .replace(/\bT[ÉE]CNICO\s+EM\b/gi, "")
    .replace(/\bTECN[ÓO]LOGO\s+EM\b/gi, "")
    .replace(/\bMOD\/?S[ÉE]RIE\s*(I{1,3}|IV|V|VI|[0-9]+)\s*[A-Z]?\b/gi, "")
    .replace(/\bM[ÓO]DULO\s*(I{1,3}|IV|V|VI|[0-9]+)\s*[A-Z]?\b/gi, "")
    .replace(/\bS[ÉE]RIE\s*(I{1,3}|IV|V|VI|[0-9]+)\s*[A-Z]?\b/gi, "")
    .replace(/\b([1-9][0-9]*)(º|°)?\s*(SEMESTRE|SEM)\b/gi, "")
    .replace(/\b([1-9][0-9]*)(º|°)?\s*ANO\b/gi, "")
    .replace(/\b(MANHA|MANHÃ|TARDE|NOITE|NOTURNO|VESPERTINO|MATUTINO|INTEGRAL)\b/gi, "")
    .replace(/\b\d{1,2}\/\d{4}\b/g, "")
    .replace(/\bREGULAR\b/gi, "")
    .replace(/\s*-\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function formatCourseDisplayName(value) {
  const clean = String(value || "").trim();
  if (!clean) return "";

  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
}

function getCourseCanonicalKey(value) {
  return normalizeText(sanitizeCourseName(value));
}

function formatCpf(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 11) return value || "Não informado";
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
}

function parseNumericValue(value) {
  if (typeof value === "number") return value;
  if (!value) return 0;

  const normalized = String(value).replace(",", ".");
  const match = normalized.match(/\d+(\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function formatLocalDate(date) {
  const day = date.toLocaleDateString("pt-BR", { day: "2-digit" });
  const month = date.toLocaleDateString("pt-BR", { month: "long" });
  const year = date.toLocaleDateString("pt-BR", { year: "numeric" });
  return `Indaiatuba, ${day} de ${month} de ${year}`;
}

export default function PdfDeclaracaoAcumulo() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = "Declaração de Acúmulo";

    return () => {
      document.title = previousTitle;
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const payload = useMemo(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }, []);

  const orgao = {
    nome: payload?.orgao?.nome || "Fundação Indaiatubana de Educação e Cultura",
    cnpj: payload?.orgao?.cnpj || "54.675.103/0001-80",
    endereco: payload?.orgao?.endereco || "Av. Engenheiro Fábio Roberto Barnabé, 3.405, Jardim Regina",
    cidade: payload?.orgao?.cidade || "Indaiatuba/SP",
    cep: payload?.orgao?.cep || "13349-003",
    telefone: payload?.orgao?.telefone || "(19) 3801-8688",
  };

  const professor = payload?.professor || payload?.professorDados?.nome || "Não informado";
  const matricula = payload?.matricula || "Não informada";
  const cpf = formatCpf(payload?.cpf || payload?.professorDados?.cpf);
  const cargoFuncao = payload?.cargoFuncao || payload?.professorDados?.cargoFuncao || "Professor(a)";
  const regimeJuridico = payload?.regimeJuridico || payload?.professorDados?.vinculo || "Não informado";
  const escolaridade = payload?.escolaridade || payload?.professorDados?.escolaridade || "Curso Superior";
  // console.log("regime> ",regimeJuridico)
  const aulas = Array.isArray(payload?.aulas) ? payload.aulas : [];
  const projetos = Array.isArray(payload?.projetos) ? payload.projetos : [];
  const unidadesPayload = Array.isArray(payload?.unidades) ? payload.unidades : [];
  const areaCursos = useMemo(() => {
    if (payload?.areaCursos) return payload.areaCursos;

    const cursosUnicos = new Map();

    aulas.forEach((aula) => {
      const cursoOriginal = aula?.turma || aula?.curso || aula?.disciplina;
      if (!cursoOriginal) return;

      const cursoLimpo = sanitizeCourseName(cursoOriginal);
      const cursoFormatado = formatCourseDisplayName(cursoLimpo);
      const chaveCanonica = getCourseCanonicalKey(cursoOriginal);
      if (!chaveCanonica || !cursoFormatado || cursosUnicos.has(chaveCanonica)) return;

      cursosUnicos.set(chaveCanonica, cursoFormatado);
    });

    return cursosUnicos.size ? [...cursosUnicos.values()].join("; ") : "Não informado";
  }, [aulas, payload?.areaCursos]);

  const atividadesPorUnidade = useMemo(() => {
    const atividades = [
      ...aulas.filter(hasRelevantAulaData).map((aula) => ({
        tipo: "aula",
        ...getUnidadeDetalhes(aula, unidadesPayload),
        diaSemana: aula?.dia_semana || "-",
        horarioInicio: aula?.horario_inicio || "-",
        horarioFim: aula?.horario_fim || "-",
        atividade: aula?.disciplina || aula?.turma || "Aula",
        numeroAulas: aula?.carga || aula?.numero_aulas || "-",
      })),
      ...projetos.filter(hasRelevantProjetoData).map((projeto) => ({
        tipo: "projeto",
        ...getUnidadeDetalhes(projeto, unidadesPayload),
        diaSemana: projeto?.dia_semana || "-",
        horarioInicio: projeto?.horario_inicio || "-",
        horarioFim: projeto?.horario_fim || "-",
        atividade: projeto?.projeto || "Projeto",
        cargaHoraria: projeto?.carga_horaria || projeto?.carga || "-",
      })),
    ];

    return atividades.reduce((acc, item) => {
      if (!acc[item.unidade]) {
        acc[item.unidade] = [];
      }
      acc[item.unidade].push(item);
      return acc;
    }, {});
  }, [aulas, projetos, unidadesPayload]);

  const unidades = Object.keys(atividadesPorUnidade);
  const localDate = formatLocalDate(new Date());
  const totalAulas = aulas.reduce((total, aula) => total + parseNumericValue(aula?.carga || aula?.numero_aulas), 0);
  const totalProjetos = projetos.reduce((total, projeto) => total + parseNumericValue(projeto?.carga_horaria || projeto?.carga), 0);
  const htpc = Math.ceil(totalAulas * 0.2);
  const totalSemanal = totalAulas + totalProjetos + htpc;

  return (
    <Background>
      <PdfButtons />
      <Page>
        <Subpage>
          <Images>
            <img src={Fiec} alt="FIEC" />
            <img src={Prefeitura} alt="Prefeitura" />
          </Images>

          <Content>
            <Title>DECLARAÇÃO DE ACÚMULO DE CARGOS/EMPREGOS PÚBLICOS</Title>

            <BlockTitle>1- Identificação do órgão</BlockTitle>
            <InfoGrid>
              <InfoItem>
                <strong>Nome:</strong> {orgao.nome}
              </InfoItem>
              <InfoItem>
                <strong>CNPJ:</strong> {orgao.cnpj}
              </InfoItem>
              <InfoItem>
                <strong>Endereço:</strong> {orgao.endereco}
              </InfoItem>
              <InfoItem>
                <strong>CEP:</strong> {orgao.cep}
              </InfoItem>
              <InfoItem>
                <strong>Cidade:</strong> {orgao.cidade}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Telefone:</strong> {orgao.telefone}
              </InfoItem>
            </InfoGrid>

            <BlockTitle>2- Identificação do servidor / empregado</BlockTitle>
            <InfoGrid>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Nome:</strong> {professor}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>CPF:</strong> {cpf}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Cargo/Função/Emprego Público:</strong>{" "}
                {regimeJuridico === "ESTATUTARIO" ? "Professor(a)" : "Professor(a) de Ensino Técnico"}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Regime Jurídico:</strong> {regimeJuridico === "ESTATUTARIO" ? "Estatutário" : "CLT"}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Área/Cursos que atua:</strong> {areaCursos}
              </InfoItem>
              <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Escolaridade exigida para exercer o cargo/função/emprego:</strong> {escolaridade}
              </InfoItem>
              {/* <InfoItem style={{ gridColumn: "1 / -1" }}>
                <strong>Matrícula:</strong> {matricula}
              </InfoItem> */}
            </InfoGrid>

            <BlockTitle>3- Identificação da(s) unidade(s)</BlockTitle>
            {unidades.length === 0 && (
              <Table>
                <tbody>
                  <tr>
                    <td>Nenhuma atividade encontrada.</td>
                  </tr>
                </tbody>
              </Table>
            )}
            {unidades.map((unidade, index) => {
              const atividadesDaUnidade = atividadesPorUnidade[unidade];
              const aulasDaUnidade = atividadesDaUnidade.filter((i) => i.tipo === "aula");
              const projetosDaUnidade = atividadesDaUnidade.filter((i) => i.tipo === "projeto");

              return (
                <UnidadeBlock key={`${unidade}-${index}`}>
                  <SectionTitle>{unidade}</SectionTitle>

                  <InfoGrid>
                    <InfoItem style={{ gridColumn: "1 / -1" }}>
                      <strong>Nome da Unidade que ministra aulas:</strong> {atividadesDaUnidade[0]?.nome || unidade}
                    </InfoItem>
                    <InfoItem style={{ gridColumn: "1 / -1" }}>
                      <strong>Endereço:</strong> {atividadesDaUnidade[0]?.endereco + " - " + atividadesDaUnidade[0]?.cidade}
                    </InfoItem>
                    <InfoItem style={{ gridColumn: "1 / -1" }}>
                      <strong>Fone:</strong> {atividadesDaUnidade[0]?.telefone}
                    </InfoItem>
                  </InfoGrid>

                  {aulasDaUnidade.length > 0 && (
                    <>
                      <SubLabel>Aulas atribuídas:</SubLabel>
                      <Table>
                        <thead>
                          <tr>
                            <th>Dia</th>
                            <th>Horário</th>
                            <th className="nome">Componente</th>
                            <th className="cargaTh">N° Aulas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aulasDaUnidade.map((item, itemIndex) => (
                            <tr key={`aula-${unidade}-${itemIndex}`}>
                              <td>{item.diaSemana}</td>
                              <td>
                                {item.horarioInicio} às {item.horarioFim}
                              </td>
                              <td>{item.atividade}</td>
                              <td className="carga">{item.numeroAulas}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {aulasDaUnidade.length === 0 && <Note>Aulas atribuídas: nenhuma aula encontrada para esta unidade.</Note>}

                  {projetosDaUnidade.length > 0 && (
                    <>
                      <SubLabel>Projetos/Atividades Extra Classe:</SubLabel>
                      <Table>
                        <thead>
                          <tr>
                            <th>Dia</th>
                            <th>Horário</th>
                            <th className="nome">Atividade</th>
                            <th className="cargaTh">Carga horária</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projetosDaUnidade.map((item, itemIndex) => (
                            <tr key={`proj-${unidade}-${itemIndex}`}>
                              <td>{item.diaSemana}</td>
                              <td>
                                {item.horarioInicio} às {item.horarioFim}
                              </td>
                              <td>{item.atividade}</td>
                              <td className="carga">{item.cargaHoraria}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}

                  {projetosDaUnidade.length === 0 && (
                    <Note>Projetos/Atividades Extra Classe: nenhum projeto ou atividade extra classe encontrado para esta unidade.</Note>
                  )}
                </UnidadeBlock>
              );
            })}

            <Paragraph>
              <strong>CARGA HORÁRIA SEMANAL:</strong> {totalAulas} aulas atribuídas, {totalProjetos} projetos/atividades extra classe e{" "}
              {htpc} HTPC (realizado em horário variável), totalizando {totalAulas}+{totalProjetos}+{htpc}={totalSemanal}.
            </Paragraph>

            <Paragraph>
              <b>
                É responsabilidade do servidor/empregado solicitar a atualização das informações sempre que houver alteração da carga
                horária.
              </b>
            </Paragraph>

            <PlaceDate>{localDate}</PlaceDate>

            <SignatureRow>
              <div>
                <span>_____________________________</span>
                <p>Assinatura e Cargo do Responsável</p>
              </div>
            </SignatureRow>
          </Content>
        </Subpage>
      </Page>
    </Background>
  );
}
